import {
  DB_COST_LICENSING_NOTE,
  DB_RECOMMENDED_NOTE,
  RDS_COST_PENDING_NOTE,
  RDS_POOL_LABEL,
  getPoolTier,
  memoryText,
  resolveVm,
  sessionsText,
  vcpuText,
  type PoolTierId,
  type VmKind,
} from '@/data/sizeCatalog'
import { useInfrastructureWizard } from '@/stores/infrastructureWizard'
import type { VmSizeCheck } from '@/types/infrastructure'
import { formatCurrency } from '@/utils/format'
import { ReadOnlyInfoCard, type InfoItem } from './ReadOnlyInfoCard'
import { VmSizeNotice } from './VmSizeNotice'

interface VmSpecCardProps {
  kind: VmKind
  /** The pool tier the user chose. It decides the sessions of the pool, never the VM. */
  poolTier: PoolTierId
  title: string
  /** One line under the values. */
  note?: string
  /** Whether the size can be built in the chosen region. For the DB VM, the nearest series of the same size is shown when its own cannot be built. */
  check?: VmSizeCheck
  /** Why the size could not be checked, when it could not. */
  checkNote?: string
}

/**
 * One VM, read-only, from the size catalog: Azure VM SKU, vCPU, memory, disk and the estimated monthly cost. The RDS VM is the one fixed size
 * for every tier and also shows the pool and the sessions of the tier chosen; its price is not set yet, and the card says so rather than show
 * a number. The DB VM is a provisional constant: it is marked "Recommended size, not confirmed", and its cost leaves out SQL licensing.
 * The SKU shown is the one that is built (the nearest series of the same size where the region cannot build the configured one, with a notice
 * under the card; Next waits until the user has chosen to continue with it, or asked for more quota).
 */
export function VmSpecCard({ kind, poolTier, title, note, check, checkNote }: VmSpecCardProps) {
  const spec = resolveVm(kind, poolTier)
  const tier = getPoolTier(poolTier)
  const regionLabel = useInfrastructureWizard((s) => s.region?.label)
  const substituted = check?.status === 'substituted'
  const cost =
    spec.estCostUsdPerMonth === null
      ? RDS_COST_PENDING_NOTE
      : `${formatCurrency(spec.estCostUsdPerMonth)}/month${substituted ? ` (for ${spec.sku})` : ''}`
  const items: InfoItem[] = [
    ...(kind === 'rds'
      ? [
          { label: 'Pool', value: RDS_POOL_LABEL },
          { label: 'Pool tier', value: `${tier.label} · ${sessionsText(tier.sessions)}` },
        ]
      : []),
    { label: kind === 'db' ? 'DB VM SKU' : 'RDS VM SKU', value: check?.effective ?? spec.sku, mono: true },
    { label: 'CPU', value: vcpuText(spec) },
    { label: 'Memory', value: memoryText(spec) },
    { label: 'Disk', value: spec.disk },
    { label: kind === 'db' ? DB_COST_LICENSING_NOTE : 'Est. monthly cost', value: cost },
  ]
  return (
    <>
      <ReadOnlyInfoCard title={title} items={items} note={[note, spec.provisional ? `${DB_RECOMMENDED_NOTE}.` : ''].filter(Boolean).join(' ')} />
      <VmSizeNotice check={check} note={checkNote} regionLabel={regionLabel} />
    </>
  )
}
